import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/browser';

import Workout from '../../../../Model/Workout';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import useToast from '../../../hooks/useToast';
import { TableTabContainer } from '../styles';
import texts from './texts.json';
import { ActionType } from './utils';
import WorkoutEditor from './WorkoutEditor';
import WorkoutsTable from './WorkoutsTable';

/** Deep-link parameter used to open one workout straight in the editor. */
const WORKOUT_ID_PARAM = 'workoutId';

const Workouts = () => {
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [actionPerformed, setActionPerformed] = useState(null);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const handleActionClick = useCallback((workout, action) => {
    setActionPerformed(action);
    setSelectedWorkout(workout);
  }, []);

  const deepLinkedWorkoutId = useMemo(() => new URLSearchParams(search).get(WORKOUT_ID_PARAM), [search]);

  /*
    Opens the workout named in the URL. This is how anything outside the editor — a shared link, or
    the long-session upload row under the table — asks for a specific workout, since the editor has
    no route of its own.
  */
  useEffect(() => {
    if (!deepLinkedWorkoutId) {
      return;
    }

    const openWorkout = async () => {
      try {
        const workoutDoc = await Workout.getWorkoutById(deepLinkedWorkoutId);
        if (!isComponentMountedRef.current) {
          return;
        }
        // Discarding archives rather than deletes, so an archived workout is as gone as a missing one.
        if (workoutDoc.exists && !workoutDoc.isArchived) {
          handleActionClick(workoutDoc, ActionType.UPDATE);
          return;
        }
        // Discarded since the link was made: say so and drop the param, or the table looks broken.
        showToast(texts.deepLinkedWorkoutMissing, { error: true });
        history.replace(pathname);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            message: 'Failed to open a deep-linked workout',
            workoutId: deepLinkedWorkoutId,
          },
        });
      }
    };

    openWorkout();
  }, [
    deepLinkedWorkoutId,
    handleActionClick,
    history,
    pathname,
    showToast,
    isComponentMountedRef,
  ]);

  const handleEditorClosed = useCallback(() => {
    handleActionClick(null, null);
    // Drop the deep link so returning to this tab does not reopen the editor.
    if (deepLinkedWorkoutId) {
      history.replace(pathname);
    }
  }, [handleActionClick, deepLinkedWorkoutId, history, pathname]);

  const showEditorView = useMemo(
    () => [ActionType.CREATE, ActionType.UPDATE].includes(actionPerformed),
    [actionPerformed],
  );

  return (
    <TableTabContainer>
      {showEditorView
        ? (
          <WorkoutEditor
            workout={selectedWorkout}
            action={actionPerformed}
            onSave={handleEditorClosed}
            onClose={handleEditorClosed}
          />
        )
        : (
          <WorkoutsTable
            handleActionClick={handleActionClick}
            selectedWorkout={selectedWorkout}
            actionPerformed={actionPerformed}
          />
        )}
    </TableTabContainer>
  );
};

export default Workouts;
